<template>
  <div class="irp-opportunity-list" data-testid="irp-opportunity-list">
    <BittsEmptyState
      v-if="!rows.length"
      class="irp-opportunity-list__full-width"
      :font-awesome-icon="EMPTY_STATE.icon"
      :title="emptyMessage"
      :description="EMPTY_STATE.description"
      data-testid="irp-opportunity-list-empty"
    />
    <template v-else>
      <template v-for="(row, index) in rows" :key="`opportunity_${index}`">
        <div
          class="irp-opportunity-list__cell"
          data-testid="irp-opportunity-name"
        >
          <span class="irp-opportunity-list__label">{{ row.name.label }}</span>
          <IRPFieldGridText :text="row.name.value" :is-masked="true" />
        </div>

        <div
          class="irp-opportunity-list__cell"
          data-testid="irp-opportunity-owner"
        >
          <span class="irp-opportunity-list__label">{{ row.owner.label }}</span>
          <div class="irp-opportunity-list__owner">
            <FontAwesomeIcon
              :icon="['fak', 'person']"
              class="irp-opportunity-list__icon"
            />
            <IRPFieldGridText :text="row.owner.value" :is-masked="true" />
          </div>
        </div>

        <div
          class="irp-opportunity-list__cell"
          data-testid="irp-opportunity-amount"
        >
          <span class="irp-opportunity-list__label">
            {{ row.amount.label }}
          </span>
          <IRPFieldGridText
            class="irp-opportunity-list__amount"
            :text="row.amount.value"
            :is-masked="true"
          />
        </div>

        <div
          class="irp-opportunity-list__cell"
          data-testid="irp-opportunity-close-date"
        >
          <span class="irp-opportunity-list__label">
            {{ row.closeDate.label }}
          </span>
          <IRPFieldGridText :text="row.closeDate.value" :is-masked="true" />
        </div>

        <div
          class="irp-opportunity-list__cell"
          data-testid="irp-opportunity-stage"
        >
          <span class="irp-opportunity-list__label">{{ row.stage.label }}</span>
          <IRPFieldGridText :text="row.stage.value" :is-masked="true" />
        </div>

        <div
          v-if="row.productNames.length"
          class="irp-opportunity-list__cell irp-opportunity-list__full-width"
          data-testid="irp-opportunity-products"
        >
          <span class="irp-opportunity-list__label">
            {{ pluralize(row.productNames.length, 'Product') }}
          </span>
          <IRPFieldGridText
            :text="row.productNames.join(', ')"
            :is-masked="true"
          />
        </div>

        <div
          v-if="row.remainingFields.length"
          class="irp-opportunity-list__full-width"
        >
          <IRPAllFieldsCollapse :fields="row.remainingFields" />
        </div>

        <div
          v-if="index < rows.length - 1"
          class="irp-opportunity-list__separator"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { BittsEmptyState } from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { pluralize } from 'humanize-plus';
import { computed } from 'vue';

import { MDM_PROPERTIES, MDM_TYPES } from '@/constants/mdm';
import type { RecordOpportunityType } from '@/types/individual_record';

import { fieldCell, remainingFields } from './irp';
import IRPAllFieldsCollapse from './IRPAllFieldsCollapse.vue';
import IRPFieldGridText from './IRPFieldGridText.vue';

const { opportunities, emptyMessage = 'No opportunities to show' } =
  defineProps<{
    opportunities: RecordOpportunityType[];
    emptyMessage?: string;
  }>();

const EMPTY_STATE: { description: string; icon: BittsIcon } = {
  description: 'Opportunities on this account will appear here.',
  icon: ['fak', 'opportunity'],
};

const DEAL_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.DEAL];

const LISTED_PROPERTIES: string[] = [
  DEAL_PROPERTIES.NAME,
  DEAL_PROPERTIES.OWNER_ID,
  DEAL_PROPERTIES.AMOUNT,
  DEAL_PROPERTIES.CLOSE_DATE,
  DEAL_PROPERTIES.STAGE,
];

const rows = computed(() =>
  opportunities.map((opportunity) => ({
    name: fieldCell(opportunity.fields, DEAL_PROPERTIES.NAME, 'Name'),
    owner: fieldCell(
      opportunity.fields,
      DEAL_PROPERTIES.OWNER_ID,
      'Account owner',
    ),
    amount: fieldCell(opportunity.fields, DEAL_PROPERTIES.AMOUNT, 'Amount'),
    closeDate: fieldCell(
      opportunity.fields,
      DEAL_PROPERTIES.CLOSE_DATE,
      'Close date',
    ),
    stage: fieldCell(opportunity.fields, DEAL_PROPERTIES.STAGE, 'Stage'),
    remainingFields: remainingFields(opportunity.fields, LISTED_PROPERTIES),
    // @ts-expect-error opportunity_products is not in the OAS yet
    productNames: opportunity.opportunity_products ?? [],
  })),
);
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

.irp-opportunity-list {
  @apply grid items-center gap-x-24 gap-y-16;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.irp-opportunity-list__full-width,
.irp-opportunity-list__separator {
  grid-column: 1 / -1;
}

.irp-opportunity-list__cell {
  @apply flex min-w-0 flex-col gap-4 text-base text-neutral-text;
}

.irp-opportunity-list__label {
  @apply truncate text-sm font-semibold text-neutral-text-weak;
}

.irp-opportunity-list__owner {
  @apply flex min-w-0 items-center gap-8;
}

.irp-opportunity-list__icon {
  @apply shrink-0 text-neutral-accent;
}

.irp-opportunity-list__amount {
  @apply font-bold text-neutral-text-strong;
}

.irp-opportunity-list__separator {
  @apply border-t border-neutral-border;
}
</style>
